import { Component, ReactNode } from 'react';

import { Translator } from 'Translator';
import { appServices } from '../../../singletons';
import showAlert from 'utils/showAlert';

interface RenderArgs extends SearchNearbyProviderState {
  triggerSearch: () => void;
}

interface SearchNearbyProviderState {
  cities: any[];
  loading: boolean;
  visible: boolean;
}

interface SearchNearbyProviderProps {
  children: (args: RenderArgs) => ReactNode;
}

export default class SearchNearbyProvider extends Component<SearchNearbyProviderProps, SearchNearbyProviderState> {

  state = {
    loading: true,
    cities: [],
    visible: true,
  };

  componentDidMount() {
    this.getPermissions()
      .then(({ location }) => [this.isAllowed(location), this.canRequestPermission(location)])
      .then(([isAllowed, canRequest]) => {
        if (isAllowed) {
          this.loadNearbyCity().then(cities => this.setState({ cities, loading: false }));
        } else {
          this.setState({ loading: false, visible: canRequest });
        }
      });
  }

  getPermissions() {
    return appServices.permissions$
      .take(1)
      .toPromise();
  }

  requestPermissions(location) {
    const { canRequestInUse, canReRequest } = location;
    const { permissionFunctions } = appServices;
    if (canRequestInUse) {
      return permissionFunctions.requestLocationBackgroundPermission();
    }
    if (canReRequest) {
      return permissionFunctions.askForLocationPermissionsAfterDenial();
    }
    return false;
  }

  isAllowed({ gpsEnabled, inUse }) {
    return gpsEnabled && inUse;
  }

  canRequestPermission({ canRequestInUse, canReRequest }) {
    return canRequestInUse || canReRequest;
  }

  loadNearbyCity() {
    return appServices.location$
      .skipWhile(({ source }) => source !== 'GPS')
      .take(1)
      .flatMap(({ location }) => this.fetchCities(location))
      .map(this.parseCities)
      .toPromise();
  }

  findNearbyCity = () => {
    this.loadNearbyCity()
      .then((cities) => {
        this.setState({ cities, loading: false });
        if (!cities.length) {
          showAlert(
            Translator.translate('Search.SearchNearby.NoExperiencesFoundMessage', {}, true, 'But luckily we are available at many other beautiful travel destinations.'),
            Translator.translate('Search.SearchNearby.NoExperiencesFoundTitle', {}, true, 'No Withlocals experiences found near you.'),
            ['close']
          );
        }
      })
      .catch(() => {
        showAlert(
          Translator.translate('Search.SearchNearby.SomethingWentWrongMessage', {}, true, 'But luckily we are available at many other beautiful travel destinations.'),
          Translator.translate('Search.SearchNearby.SomethingWentWrongTitle', {}, true, 'Something has gone wrong.'),
          ['close']
        );
      });

  };

  fetchCities({ latitude, longitude }) {
    return fetch(`${URL_API_HOST}/api/v1/geocode/closest-cities/${latitude}/${longitude}/100`)
      .then(r => r.json());
  }

  parseCities = (cities = []) => {
    return cities
      .filter(city => city.location.area === city.location.city)
      .map(item => ({
        city: item.location.city,
        country: item.location.country,
        distanceTo: item.distanceTo,
      }));
  };

  triggerSearch = () => {
    this.setState({ loading: true }, () => {
      this.getPermissions()
        .then(({ location }) => {
          if (!this.isAllowed(location)) {
            return this.requestPermissions(location);
          }
          return true;
        })
        .then((isAllowed) => {
          if (isAllowed) {
            this.findNearbyCity();
          } else {
            this.setState({ loading: false, visible: isAllowed });
          }
        });
    });
  };

  render() {
    if (this.state.visible) {
      return this.props.children({ triggerSearch: this.triggerSearch, ...this.state });
    }
    return null;
  }

}
